/**
 * Athlete average running pace — stored as seconds per mile on
 * `CustomerPreferences.averageRunningPaceSecondsPerMile`. UI shows m:ss in the
 * athlete's distance unit.
 *
 * Bounds: ~3:00/mi floor (faster than the mile WR ≈ 3:43) through 60:00/mi so
 * very slow / adaptive athletes still fit.
 */

export const MIN_RUNNING_PACE_SEC_PER_MI = 3 * 60;
export const MAX_RUNNING_PACE_SEC_PER_MI = 60 * 60;

const MI_PER_KM = 0.6213711922;

export function isValidRunningPaceSecPerMi(sec: unknown): sec is number {
  return (
    typeof sec === "number" &&
    Number.isInteger(sec) &&
    sec >= MIN_RUNNING_PACE_SEC_PER_MI &&
    sec <= MAX_RUNNING_PACE_SEC_PER_MI
  );
}

/** Seconds-per-mile ↔ seconds-per-km for display / input in the athlete's unit. */
export function secPerMiToSecPerKm(secPerMi: number): number {
  return Math.round(secPerMi * MI_PER_KM);
}

export function secPerKmToSecPerMi(secPerKm: number): number {
  return Math.round(secPerKm / MI_PER_KM);
}

/** Format stored sec/mi as `m:ss` in the given unit. */
export function formatRunningPace(
  secPerMi: number | null | undefined,
  unit: "mi" | "km" = "mi"
): string {
  if (secPerMi == null || !Number.isFinite(secPerMi)) return "—";
  const total = unit === "km" ? secPerMiToSecPerKm(secPerMi) : Math.round(secPerMi);
  if (total < 0) return "—";
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/**
 * Parse a `m:ss` / `m.ss` / bare-minutes string in the given unit into stored
 * sec/mi. Null when empty or unparseable; out-of-range values return
 * `{ ok: false, reason: "range" }`.
 */
export function parseRunningPaceInput(
  raw: string,
  unit: "mi" | "km" = "mi"
):
  | { ok: true; secPerMi: number }
  | { ok: false; reason: "empty" | "invalid" | "range" } {
  const text = raw.trim();
  if (!text) return { ok: false, reason: "empty" };

  let totalSec: number | null = null;
  const colon = text.match(/^(\d{1,2}):([0-5]?\d)$/);
  if (colon) {
    totalSec = Number(colon[1]) * 60 + Number(colon[2]);
  } else {
    const bare = text.match(/^(\d{1,2})(?:\.(\d{1,2}))?$/);
    if (bare) {
      const mins = Number(bare[1]);
      const frac = bare[2] != null ? Number(bare[2].padEnd(2, "0").slice(0, 2)) : 0;
      if (frac >= 60) return { ok: false, reason: "invalid" };
      totalSec = mins * 60 + frac;
    }
  }
  if (totalSec == null || !Number.isFinite(totalSec)) return { ok: false, reason: "invalid" };

  const secPerMi = unit === "km" ? secPerKmToSecPerMi(totalSec) : totalSec;
  if (!isValidRunningPaceSecPerMi(secPerMi)) return { ok: false, reason: "range" };
  return { ok: true, secPerMi };
}

export function runningPaceRangeHint(unit: "mi" | "km" = "mi"): string {
  const lo = formatRunningPace(MIN_RUNNING_PACE_SEC_PER_MI, unit);
  const hi = formatRunningPace(MAX_RUNNING_PACE_SEC_PER_MI, unit);
  return `${lo}–${hi} /${unit}`;
}

/** Split stored sec/mi into display-unit minutes + seconds for two-box inputs. */
export function splitRunningPaceParts(
  secPerMi: number | null | undefined,
  unit: "mi" | "km" = "mi"
): { minutes: number; seconds: number } | null {
  if (secPerMi == null || !Number.isFinite(secPerMi)) return null;
  const total = unit === "km" ? secPerMiToSecPerKm(secPerMi) : Math.round(secPerMi);
  if (total < 0) return null;
  return { minutes: Math.floor(total / 60), seconds: total % 60 };
}

/**
 * Combine minutes + seconds (in the athlete's unit) into stored sec/mi.
 * Empty both → empty; partial / out-of-range → invalid or range.
 */
export function combineRunningPaceParts(
  minutesText: string,
  secondsText: string,
  unit: "mi" | "km" = "mi"
):
  | { ok: true; secPerMi: number }
  | { ok: false; reason: "empty" | "invalid" | "range" } {
  const mRaw = minutesText.trim();
  const sRaw = secondsText.trim();
  if (!mRaw && !sRaw) return { ok: false, reason: "empty" };
  if (!/^\d{1,2}$/.test(mRaw) || (sRaw !== "" && !/^\d{1,2}$/.test(sRaw))) {
    return { ok: false, reason: "invalid" };
  }
  const minutes = Number(mRaw);
  const seconds = sRaw === "" ? 0 : Number(sRaw);
  if (!Number.isInteger(minutes) || !Number.isInteger(seconds) || seconds > 59) {
    return { ok: false, reason: "invalid" };
  }
  const totalSec = minutes * 60 + seconds;
  const secPerMi = unit === "km" ? secPerKmToSecPerMi(totalSec) : totalSec;
  if (!isValidRunningPaceSecPerMi(secPerMi)) return { ok: false, reason: "range" };
  return { ok: true, secPerMi };
}

/** Spinner step for the weight-style pace control (5 seconds). */
export const RUNNING_PACE_STEP_SEC = 5;

/**
 * Digit-buffer → display-unit seconds. Digits are read right-to-left as a
 * stopwatch: `1130` → 11 min 30 sec. Empty → null.
 */
export function displaySecsFromDigitBuffer(digits: string): number | null {
  const d = digits.replace(/\D/g, "").slice(-4);
  if (!d) return null;
  const padded = d.padStart(4, "0");
  const minutes = Number(padded.slice(0, 2));
  const seconds = Number(padded.slice(2));
  if (seconds > 59) return null;
  return minutes * 60 + seconds;
}

/** Clamp a display-unit total-seconds value into the stored sec/mi range. */
export function clampRunningPaceSecPerMi(secPerMi: number): number {
  return Math.min(
    MAX_RUNNING_PACE_SEC_PER_MI,
    Math.max(MIN_RUNNING_PACE_SEC_PER_MI, Math.round(secPerMi))
  );
}

/** Step stored sec/mi by ±step display-seconds (unit-aware). */
export function stepRunningPaceSecPerMi(
  secPerMi: number | null,
  direction: 1 | -1,
  unit: "mi" | "km" = "mi",
  stepSec: number = RUNNING_PACE_STEP_SEC
): number {
  const base =
    secPerMi != null && Number.isFinite(secPerMi)
      ? secPerMi
      : unit === "km"
        ? secPerKmToSecPerMi(10 * 60)
        : 10 * 60;
  const display = unit === "km" ? secPerMiToSecPerKm(base) : base;
  const nextDisplay = display + direction * stepSec;
  const nextStored = unit === "km" ? secPerKmToSecPerMi(nextDisplay) : nextDisplay;
  return clampRunningPaceSecPerMi(nextStored);
}

function hasPositive(n: number | null | undefined): n is number {
  return n != null && Number.isFinite(n) && n > 0;
}

/**
 * Distance (km) + average pace (sec/mi) → planned duration in minutes.
 * Null when inputs are unusable.
 */
export function durationMinFromDistanceKm(
  distanceKm: number,
  secPerMi: number
): number | null {
  if (!isValidRunningPaceSecPerMi(secPerMi) || !hasPositive(distanceKm)) return null;
  const minutes = (distanceKm * MI_PER_KM * secPerMi) / 60;
  if (!Number.isFinite(minutes) || minutes <= 0) return null;
  return Math.max(1, Math.round(minutes));
}

/**
 * Duration (min) + average pace (sec/mi) → planned distance in km.
 * Null when inputs are unusable.
 */
export function distanceKmFromDurationMin(
  durationMin: number,
  secPerMi: number
): number | null {
  if (!isValidRunningPaceSecPerMi(secPerMi) || !hasPositive(durationMin)) return null;
  const miles = (durationMin * 60) / secPerMi;
  const km = miles / MI_PER_KM;
  if (!Number.isFinite(km) || km <= 0) return null;
  return Math.round(km * 1000) / 1000;
}

export type PaceEstimateFields = {
  sport: string;
  dayOff?: boolean;
  durationMin: number | null;
  distanceKm: number | null;
};

/**
 * For `sport === "run"` only: fill a missing duration from distance (or missing
 * distance from duration) using the athlete's average running pace. When both
 * or neither are present, returns the fields unchanged. Non-runs and day-offs
 * are never estimated.
 */
export function applyRunningPaceEstimates<T extends PaceEstimateFields>(
  fields: T,
  secPerMi: number | null | undefined
): T {
  if (!isValidRunningPaceSecPerMi(secPerMi)) return fields;
  if (fields.dayOff === true || fields.sport !== "run") return fields;

  const hasDuration = hasPositive(fields.durationMin);
  const hasDistance = hasPositive(fields.distanceKm);
  if (hasDuration === hasDistance) return fields; // both or neither — leave alone

  if (hasDistance && !hasDuration) {
    const durationMin = durationMinFromDistanceKm(fields.distanceKm!, secPerMi);
    return durationMin == null ? fields : { ...fields, durationMin };
  }

  const distanceKm = distanceKmFromDurationMin(fields.durationMin!, secPerMi);
  return distanceKm == null ? fields : { ...fields, distanceKm };
}
